import { MouseEvent as ReactMouseEvent, useCallback, useMemo } from 'react';
import { ThreeEvent } from '@react-three/fiber';

import { isTruss } from '@paragon/contract-library';
import { getModifierKeys } from '@paragon/utilities';

import { useAppDispatch } from '../../components/hooks';
import {
  SelectableItemMap,
  SelectableItemOfType,
  SelectableItemType,
  SelectedItemIdentifierOfType,
} from '../../library/selection/types';
import { scrollTo } from '../../scrolling';
import { threeJsDom } from '../../three/util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../three/util/events/types';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getCurrentlyRightClickableItemTypes } from '../selectors/getCurrentlyClickableItemTypes';
import { getSelectedEntryFactory } from '../selectors/getSelectedItemSelector';
import { getPickingObjectInfo } from '../selectors/pickingSelectors';
import { applicationOnSetInsertionGussetCenterPoint, clearPickingObject } from '../slices/applicationSlice';
import { rightToolBarShowSet } from '../slices/panelDataSlice';
import { isRightClickableItemType } from '../slices/viewportSlice';
import { requestContextMenu } from './contextMenuActions';
import { setLocalCoordinateSystemFromMember, setLocalCoordinateSystemFromPlatePair } from './editorActions';
import {
  finishInsertingBraceMember,
  finishSetGablingStartFromProfileJoint,
  updateAndPotentiallyCommitInsertionPlatePair,
} from './insertActions';
import { resetSelectedItems, selectItem, selectItems } from './selectActions';
import { AsyncThunkActionReturn, ThunkActionVoid } from './thunkActions';

type ThreeMouseEvent = ThreeEvent<MouseEvent>;

type OriginTag = { origin: 'dom' | 'fiber' };

type TaggedItem =
  // reference lines are excluded because their context menu is exclusively for selection disambiguation and is not
  //   initiated by this action
  | {
      [Type in Exclude<SelectableItemType, 'referenceLine'>]: SelectedItemIdentifierOfType<Type> & OriginTag;
    }[Exclude<SelectableItemType, 'referenceLine'>]

  // these are not selectable items, but are right-clickable in the component panel
  | ({ type: 'additionalLoads' } & OriginTag)
  | ({ type: 'analogModel' } & OriginTag);

/** Returns a boolean indicating whether a menu was actually opened. */
export const handleStandardSceneItemRightClick =
  (
    event: ThreeMouseEvent | ReactMouseEvent | GlobalThreeJSDomObjectEvent,
    item: TaggedItem | null,
  ): AsyncThunkActionReturn<boolean> =>
  async (dispatch, getState) => {
    switch (item?.origin) {
      case 'dom': {
        event = event as ReactMouseEvent;

        event.preventDefault();
        event.stopPropagation();

        break;
      }

      case 'fiber': {
        event = event as ThreeMouseEvent;

        if (!threeJsDom.shouldDispatchContextMenuEvent()) return false;

        event.stopPropagation();
        event.nativeEvent.preventDefault();
        event.nativeEvent.stopPropagation();

        break;
      }
    }

    if (item == null) {
      dispatch(resetSelectedItems());
      dispatch(requestContextMenu(event, { type: null }));

      return false;
    }

    if (!isRightClickableItemType(item.type)) return false;

    const state = getState();

    const currentlyRightClickableItems = getCurrentlyRightClickableItemTypes(state);
    if (!currentlyRightClickableItems.has(item.type)) return false;

    if (item.type === 'additionalLoads' || item.type === 'analogModel') {
      dispatch(requestContextMenu(event, { type: item.type }));
      return true;
    }

    const entry = getSelectedEntryFactory(state)(item);
    if (entry == null) throw new Error('Right clicked item could not be found in state.');

    // we've already verified that the item is actually right-clickable
    const rightClickedItem = { type: item.type, guid: item.guid };
    dispatch(requestContextMenu(event, rightClickedItem, { outsideScene: item.origin === 'dom' }));

    return true;
  };

export const handleStandardSceneItemEditPropertiesAction =
  <T extends SelectableItemType>(type: T, itemObject: SelectableItemOfType<T>): ThunkActionVoid =>
  (dispatch) => {
    dispatch(selectItem(itemObject, type));
    dispatch(rightToolBarShowSet(true));
    scrollTo('selectedItemProperties');
  };

export const handleStandardSceneItemsEditPropertiesAction =
  <T extends SelectableItemType>(type: T, itemObjects: SelectableItemOfType<T>[]): ThunkActionVoid =>
  (dispatch) => {
    dispatch(selectItems(itemObjects, type));
    dispatch(rightToolBarShowSet(true));
    scrollTo('selectedItemProperties');
  };

export const leftClickItem =
  (event: ThreeEvent<MouseEvent>, result: RaycastItem): ThunkActionVoid =>
  (dispatch, getState) => {
    if (event.detail === 2) {
      event.stopPropagation();
      dispatch(handleStandardSceneItemEditPropertiesAction(result.type, result.item));
      return;
    }

    if (!threeJsDom.shouldDispatchClickEvent()) return;
    event.stopPropagation();

    const state = getState();

    // When the insertion panel is open, clicking an item should select it in the insertion panel (if applicable).
    if (state.application.insertingObjectInfo?.type === 'platePair') {
      if (result.type === 'member') {
        dispatch(
          updateAndPotentiallyCommitInsertionPlatePair({
            position: { x: event.point.x, y: event.point.y },
            memberGuid: result.item.guid,
          }),
        );
        return;
      }

      if (result.type === 'splice') {
        dispatch(
          updateAndPotentiallyCommitInsertionPlatePair({
            position: { x: result.item.centerPointX, y: result.item.centerPointY },
            memberGuid: result.item.neighbor1Guid,
          }),
        );
        return;
      }
    }

    const pickingObjectInfo = getPickingObjectInfo(state);

    // Selecting a member to brace while inserting a brace
    if (pickingObjectInfo?.type === 'pickingBraceLocation' && result.type === 'member') {
      const memberToBrace = result.item;
      dispatch(finishInsertingBraceMember(memberToBrace, { x: event.point.x, y: event.point.y }));
      return;
    }

    if (pickingObjectInfo?.type === 'pickingGussetLocation') {
      dispatch(applicationOnSetInsertionGussetCenterPoint({ x: event.point.x, y: event.point.y }));
      dispatch(clearPickingObject());
      return;
    }

    if (pickingObjectInfo?.type === 'pickingProfileJoint') {
      if (result.type === 'profileJoint') {
        const componentDesign = getActiveComponentDesign(state);
        if (!componentDesign || !isTruss(componentDesign.component)) {
          throw new Error('Component design not found');
        }
        const selectedProfileJoint = componentDesign.profile?.profileJoints.find(
          (joint) => joint.guid === result.item.guid,
        );
        if (!selectedProfileJoint) {
          throw new Error('Profile joint not found');
        }
        const gableStartLocation = selectedProfileJoint.location.x;
        dispatch(finishSetGablingStartFromProfileJoint(gableStartLocation));
      }
      return;
    }

    // When the user is in the process of switching to object coordinate mode, selecting an item should do that.
    if (state.editor.getObjectCoordinate) {
      switch (result.type) {
        case 'member':
          const componentDesign = getActiveComponentDesign(state)!;
          dispatch(setLocalCoordinateSystemFromMember(componentDesign.component, result.item));
          break;
        case 'platePair':
          dispatch(setLocalCoordinateSystemFromPlatePair(result.item));
          break;
      }
      return;
    }

    // If no special cases have triggered, perform the default action: toggling the selection of the item normally.
    dispatch(selectItem(result.item, result.type, { intent: 'toggle', exclusive: !getModifierKeys(event).ctrlKey }));
  };

type RaycastItemMap = Pick<
  SelectableItemMap,
  | 'analogJoint'
  | 'analogLink'
  | 'bearing'
  | 'brace'
  | 'gusset'
  | 'hanger'
  | 'member'
  | 'panel'
  | 'platePair'
  | 'profileJoint'
  | 'referenceLine'
  | 'splice'
  | 'webJoint'
  | 'webJointSegment'
  | 'webSegment'
>;

export type RaycastItem = {
  [T in keyof RaycastItemMap]: { type: T; item: RaycastItemMap[T] };
}[keyof RaycastItemMap];

export const useClickItem = ({ type, item }: RaycastItem) => {
  const dispatch = useAppDispatch();
  const raycastResult = useMemo(() => ({ item, type }) as RaycastItem, [item, type]);

  return useCallback(
    (event: ThreeEvent<MouseEvent>) => dispatch(leftClickItem(event, raycastResult)),
    [dispatch, raycastResult],
  );
};
